import { store } from "../store";
import { CreateTeamParams, Team } from "../types/teams.types";
import { api } from "../http";
import { DefaultRes } from "../types";
import { AxiosError, AxiosResponse } from "axios";
import { RESPONSE_CODES } from "../constants/responseCodes.constants";

export class TeamService {
    public static readonly dispatch = store.dispatch.teams;
    public static readonly state = store.getState();

    /**
     * Fetches all teams.
     * @param callback Optional callback for handling responses.
     * @returns The fetched teams.
     */
    public static async fetchAll(
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Team[] | void> {
        const {
            dispatch: { toggleLoading, setTeams },
        } = TeamService;

        toggleLoading("fetchAll", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<Team[]>,
                AxiosResponse<DefaultRes<Team[]>>
            >(`teams/fetch-all-teams-users`);

            toggleLoading("fetchAll", false);
            const typedResult = Array.isArray(result) ? result : [];

            if (isValid) {
                setTeams(typedResult);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return typedResult;
        } catch (error: any) {
            toggleLoading("fetchAll", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Team>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Creates a new team by calling "teams/create-team" endpoint.
     * @param team The team object to be created.
     * @param callback Optional callback function for handling response.
     * @returns The newly created team if successful.
     */
    public static async createTeam(
        team: CreateTeamParams,
        callback?: (isValid?: boolean, message?: string, newTeam?: Team) => void
    ): Promise<Team | void> {
        const {
            dispatch: { toggleLoading, setTeams },
        } = TeamService;

        toggleLoading("createTeam", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Team>,
                AxiosResponse<DefaultRes<Team>>
            >(`teams/create-team`, team);

            toggleLoading("createTeam", false);

            if (isValid) {
                const state = store.getState();
                const newItems = [...state.teams.items, result];
                setTeams(newItems);
                // Removed: this.setTeam(result) - don't auto-select newly created teams

                // 2. Also update groups if the new team belongs to the currently selected group
                const selectedGroup = state.groups.selectedGroup;

                if (selectedGroup && selectedGroup.id === result.groupId) {
                    const updatedGroup = {
                        ...selectedGroup,
                        teamIds: [...(selectedGroup.teamIds || []), result.id],
                        teams: [...selectedGroup.teams, result]
                    };
                    store.dispatch.groups.setSelectedGroup(updatedGroup);
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message], result);
            return result;
        } catch (error: any) {
            toggleLoading("createTeam", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Team>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
     * Updates a team by calling "teams/update-team" endpoint.
     * @param team The team object to be updated.
     * @param callback Optional callback function for handling response.
     * @returns The updated team object if successful.
     */
    public static async updateTeam(
        team: Team,
        updates: CreateTeamParams,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Team | void> {
        const {
            dispatch: { toggleLoading, updateTeamInStore },
        } = TeamService;

        toggleLoading("updateTeam", true);
        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Team>,
                AxiosResponse<DefaultRes<Team>>
            >(`teams/update-team/${team.id!}`, updates);

            toggleLoading("updateTeam", false);

            if (isValid) {
                updateTeamInStore(result);
                this.setTeam(result);

                const state = store.getState()
                const selectedGroup = state.groups.selectedGroup
                if (selectedGroup && result.groupId === selectedGroup.id) {
                    // Ensure the team ID is listed (only add if not present)
                    const alreadyInGroup = selectedGroup.teamIds?.includes(result.id);

                    const updatedGroup = {
                        ...selectedGroup,
                        teamIds: alreadyInGroup
                            ? selectedGroup.teamIds
                            : [...(selectedGroup.teamIds || []), result.id],

                        teams: alreadyInGroup
                            ? selectedGroup.teams
                            : [...(selectedGroup.teams || []), result],
                    };
                    store.dispatch.groups.setSelectedGroup(updatedGroup);
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("updateTeam", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Team>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }
            callback && callback(false, String(error));
        }
    }

    /**
 * Deletes a team by calling the "teams/delete-team" endpoint.
 * @param teamId The ID of the team to be deleted.
 * @param callback Optional callback function for handling response.
 * @returns The deleted team object if successful.
 */
    public static async deleteTeam(
        teamId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Team | void> {
        const {
            dispatch: { toggleLoading, setTeams },
        } = TeamService;

        toggleLoading("deleteTeam", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Team>,
                AxiosResponse<DefaultRes<Team>>
            >(`teams/delete-team/${teamId}`);

            toggleLoading("deleteTeam", false);

            if (isValid) {
                // console.log('isValid')
                const state = store.getState();
                const filtered = state.teams.items.filter((t: Team) => t.id !== teamId);
                setTeams(filtered);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("deleteTeam", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Team>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }


    /**
     * Sets the selected team in the store.
     * @param team The team object to set.
     */
    public static setTeam(team: Team | null) {
        const {
            dispatch: { setSelectedTeam },
        } = TeamService;

        if (team != null) {
            const newTeam: Team = structuredClone(team);
            setSelectedTeam(newTeam);
        } else {
            setSelectedTeam(null);
        }
    }

    public static async changeTeamGroup(
        teamId: string,
        newGroupId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<Team | void> {
        const {
            dispatch: { toggleLoading, updateTeamInStore },
        } = TeamService;

        toggleLoading("updateTeam", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<Team>,
                AxiosResponse<DefaultRes<Team>>
            >(`teams/change-group/${teamId}`, { newGroupId });

            toggleLoading("updateTeam", false);

            if (isValid) {
                updateTeamInStore(result);
                this.setTeam(result);
            }

            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("updateTeam", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<Team>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

}
